import React, { useState, useEffect } from 'react';
import { X, Printer, Loader2, CheckCircle, AlertCircle, FileText, Tag, Package } from 'lucide-react';
import printNodeService from '../../../../../services/printNodeService';
import reportService, {
  ReportType,
  PaperSize,
  ShelfLabelContentType,
  getPaperSizeDisplayName,
  getShelfLabelContentTypeDisplayName,
  getReportTypeDisplayName,
  PrintReportRequest
} from '../../../../../services/reportService';
import type { Printer as PrinterType, Computer } from '../../../../../types/printNode';
import type { PListDetailsDto } from '../../../../../types/picking';

interface PrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyNo: string;
  documentTitle?: string;
  pListId?: number;
  pickingDetails?: PListDetailsDto | null; // Data fra pågående plukk
}

export const PrintModal: React.FC<PrintModalProps> = ({
  isOpen,
  onClose,
  companyNo,
  documentTitle = 'Dokument',
  pListId,
  pickingDetails
}) => {
  const [computers, setComputers] = useState<Computer[]>([]);
  const [printers, setPrinters] = useState<PrinterType[]>([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPrinters, setLoadingPrinters] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [copies, setCopies] = useState(1);

  // Report configuration
  const [selectedReportType, setSelectedReportType] = useState<ReportType>(ReportType.ShelfLabel);
  const [selectedPaperSize, setSelectedPaperSize] = useState<PaperSize>(PaperSize.Label_100x50mm);

  // Shelf label fields
  const [shelfLabelContentType, setShelfLabelContentType] = useState<ShelfLabelContentType>(ShelfLabelContentType.ItemNumberWithBarcode);
  const [shelfItemNo, setShelfItemNo] = useState('');
  const [shelfItemDescription, setShelfItemDescription] = useState('');
  const [shelfLocationName, setShelfLocationName] = useState('');
  const [shelfBinCode, setShelfBinCode] = useState('');

  // Item label fields
  const [itemNo, setItemNo] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemBatch, setItemBatch] = useState('');
  const [itemQuantity, setItemQuantity] = useState<number | undefined>();

  // Colli label fields
  const [colliOrderNoERP, setColliOrderNoERP] = useState('');
  const [colliCustomerName, setColliCustomerName] = useState('');
  const [colliDeliveryAddress, setColliDeliveryAddress] = useState('');
  const [colliPostalCode, setColliPostalCode] = useState('');
  const [colliCity, setColliCity] = useState('');
  const [colliNumber, setColliNumber] = useState(1);
  const [colliTotal, setColliTotal] = useState(1);
  const [colliSSCC, setColliSSCC] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadPrinters();
      resetForm();
      preFillFromPickingDetails();
    }
  }, [isOpen, companyNo]);

  // Update paper size defaults when report type changes
  useEffect(() => {
    switch (selectedReportType) {
      case ReportType.ShelfLabel:
      case ReportType.ItemLabel:
        setSelectedPaperSize(PaperSize.Label_100x50mm);
        break;
      case ReportType.ColliLabel:
        setSelectedPaperSize(PaperSize.A6);
        break;
      case ReportType.PackingSlip:
        setSelectedPaperSize(PaperSize.A4);
        break;
    }
  }, [selectedReportType]);

  // Pre-fill data when pickingDetails changes
  useEffect(() => {
    if (pickingDetails && isOpen) {
      preFillFromPickingDetails();
    }
  }, [pickingDetails, isOpen]);

  const resetForm = () => {
    setShelfItemNo('');
    setShelfItemDescription('');
    setShelfLocationName('');
    setShelfBinCode('');
    setItemNo('');
    setItemDescription('');
    setItemBatch('');
    setItemQuantity(undefined);
    setColliOrderNoERP('');
    setColliCustomerName('');
    setColliDeliveryAddress('');
    setColliPostalCode('');
    setColliCity('');
    setColliNumber(1);
    setColliTotal(1);
    setColliSSCC('');
    setError(null);
    setSuccess(false);
  };

  const preFillFromPickingDetails = () => {
    if (!pickingDetails || !pickingDetails.lines || pickingDetails.lines.length === 0) {
      return;
    }

    // Ta første linje som har data
    const firstLine = pickingDetails.lines[0];
    const firstCompletedLine = pickingDetails.lines.find(l => l.qtyPicked > 0) || firstLine;

    // Pre-fill hyllekant/vareetikett data
    if (firstCompletedLine.itemNo) {
      setShelfItemNo(firstCompletedLine.itemNo);
      setItemNo(firstCompletedLine.itemNo);
      // Vi har ikke itemDescription i PListLineDto, men vi kan sette det vi har
    }

    if (firstCompletedLine.binCode) {
      setShelfBinCode(firstCompletedLine.binCode);
      setShelfLocationName(firstCompletedLine.binCode);
    }

    if (firstCompletedLine.qtyPicked > 0) {
      setItemQuantity(firstCompletedLine.qtyPicked);
    }

    // Pre-fill kollietikett data
    if (firstLine.orderNoERP) {
      setColliOrderNoERP(firstLine.orderNoERP);
    }

    if (firstLine.customerName) {
      setColliCustomerName(firstLine.customerName);
    }

    // Beregn totalt antall kolli basert på antall unike ordrer
    const uniqueOrders = new Set(pickingDetails.lines.map(l => l.orderNo));
    setColliTotal(uniqueOrders.size);
  };

  const loadPrinters = async () => {
    if (!companyNo) {
      setError('CompanyNo mangler. Vennligst logg inn på nytt.');
      setLoadingPrinters(false);
      return;
    }

    setLoadingPrinters(true);
    setError(null);
    try {
      const [computersData, printersData] = await Promise.all([
        printNodeService.getComputers(companyNo),
        printNodeService.getPrinters(companyNo)
      ]);
      setComputers(computersData);
      setPrinters(printersData);

      if (printersData.length > 0) {
        setSelectedPrinterId(printersData[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load printers:', err);
      setError(`Kunne ikke laste skrivere: ${err.message}`);
    } finally {
      setLoadingPrinters(false);
    }
  };

  const handlePrint = async () => {
    if (!selectedPrinterId) {
      setError('Vennligst velg en skriver');
      return;
    }

    // Validate fields based on report type
    if (selectedReportType === ReportType.ShelfLabel) {
      if (shelfLabelContentType === ShelfLabelContentType.ItemNumberOnly ||
          shelfLabelContentType === ShelfLabelContentType.ItemNumberWithBarcode) {
        if (!shelfItemNo) {
          setError('Vennligst fyll ut artikkelnummer');
          return;
        }
      } else {
        if (!shelfLocationName && !shelfBinCode) {
          setError('Vennligst fyll ut lokasjonsnavn eller lokasjonskode');
          return;
        }
      }
    } else if (selectedReportType === ReportType.ItemLabel) {
      if (!itemNo || !itemDescription) {
        setError('Vennligst fyll ut artikkelnummer og beskrivelse');
        return;
      }
    } else if (selectedReportType === ReportType.ColliLabel) {
      if (!colliOrderNoERP || !colliCustomerName) {
        setError('Vennligst fyll ut ordrenummer og kundenavn');
        return;
      }
    } else if (selectedReportType === ReportType.PackingSlip) {
      if (!pListId) {
        setError('Pakkseddel krever en plukkliste-ID');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const request: PrintReportRequest = {
        printerId: selectedPrinterId,
        reportType: selectedReportType,
        copies: copies
      };

      // Add type-specific data
      if (selectedReportType === ReportType.ShelfLabel) {
        request.shelfLabel = {
          contentType: shelfLabelContentType,
          paperSize: selectedPaperSize,
          itemNo: shelfItemNo || undefined,
          itemDescription: shelfItemDescription || undefined,
          locationName: shelfLocationName || undefined,
          binCode: shelfBinCode || undefined
        };
      } else if (selectedReportType === ReportType.ItemLabel) {
        request.itemLabel = {
          paperSize: selectedPaperSize,
          itemNo,
          itemDescription,
          batch: itemBatch || undefined,
          quantity: itemQuantity
        };
      } else if (selectedReportType === ReportType.ColliLabel) {
        request.colliLabel = {
          paperSize: selectedPaperSize,
          orderNoERP: colliOrderNoERP,
          customerName: colliCustomerName,
          deliveryAddress: colliDeliveryAddress || undefined,
          postalCode: colliPostalCode || undefined,
          city: colliCity || undefined,
          colliNumber,
          totalColli: colliTotal,
          sscc: colliSSCC || undefined
        };
      } else if (selectedReportType === ReportType.PackingSlip) {
        // Bruk faktiske data fra pickingDetails hvis tilgjengelig
        if (pickingDetails && pickingDetails.lines && pickingDetails.lines.length > 0) {
          const uniqueCustomers = new Set(pickingDetails.lines.map(l => l.customerName).filter(c => c));
          const customerName = uniqueCustomers.size === 1
            ? pickingDetails.lines[0].customerName || 'Ukjent kunde'
            : `${uniqueCustomers.size} kunder`;

          const totalWeight = pickingDetails.lines.reduce((sum, line) => sum + (line.qtyPicked || 0), 0) * 0.5; // Estimert vekt
          const uniqueOrders = new Set(pickingDetails.lines.map(l => l.orderNo));

          request.packingSlip = {
            paperSize: selectedPaperSize,
            pListId: pListId || 0,
            companyNo: companyNo,
            packingDate: pickingDetails.header.pListDate || new Date().toISOString(),
            customerName: customerName,
            lines: pickingDetails.lines.map((line, index) => ({
              lineNo: index + 1,
              itemNo: line.itemNo || 'N/A',
              itemDescription: `Linje ${line.lineNo}`,
              qtyOrdered: line.qty,
              qtyPicked: line.qtyPicked,
              location: line.binCode || ''
            })),
            totalWeight: totalWeight,
            totalColli: uniqueOrders.size,
            remarks: `Plukkliste #${pickingDetails.header.pListID} - ${pickingDetails.header.statusText}`
          };
        } else {
          // Fallback til demo-data hvis ingen pickingDetails
          request.packingSlip = {
            paperSize: selectedPaperSize,
            pListId: pListId || 0,
            companyNo: companyNo,
            packingDate: new Date().toISOString(),
            customerName: 'Demo Kunde AS',
            deliveryAddress: 'Testveien 123, 0123 Oslo',
            lines: [
              { lineNo: 1, itemNo: 'ITEM001', itemDescription: 'Demo produkt 1', qtyOrdered: 10, qtyPicked: 10, location: 'A-01-01' },
              { lineNo: 2, itemNo: 'ITEM002', itemDescription: 'Demo produkt 2', qtyOrdered: 5, qtyPicked: 5, location: 'A-01-02' }
            ],
            totalWeight: 25.5,
            totalColli: 2,
            carrierName: 'PostNord',
            remarks: 'Demo pakkseddel'
          };
        }
      }

      const response = await reportService.printReport(companyNo, request);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          resetForm();
        }, 2000);
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      console.error('Print failed:', err);
      setError(`Utskrift feilet: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getComputerName = (computerId: number): string => {
    const computer = computers.find(c => c.id === computerId);
    return computer?.name || 'Ukjent';
  };

  const renderReportTypeSelector = () => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Rapporttype
      </label>
      <div className="grid grid-cols-2 gap-3">
        {[
          { type: ReportType.ShelfLabel, label: 'Hyllekant', icon: <Tag size={18} /> },
          { type: ReportType.ItemLabel, label: 'Vareetikett', icon: <Tag size={18} /> },
          { type: ReportType.ColliLabel, label: 'Kolli', icon: <Package size={18} /> },
          { type: ReportType.PackingSlip, label: 'Pakkseddel', icon: <FileText size={18} /> }
        ].map(({ type, label, icon }) => (
          <button
            key={type}
            onClick={() => setSelectedReportType(type)}
            className={`flex items-center gap-2 px-4 py-3 min-h-[44px] border rounded-lg transition-all ${
              selectedReportType === type
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {icon}
            <span className="font-medium">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderPaperSizeSelector = () => {
    const availableSizes = [
      PaperSize.Label_100x50mm,
      PaperSize.Label_100x150mm,
      PaperSize.A6,
      PaperSize.A4
    ];

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Papirstørrelse
        </label>
        <select
          value={selectedPaperSize}
          onChange={(e) => setSelectedPaperSize(Number(e.target.value) as PaperSize)}
          className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {availableSizes.map((size) => (
            <option key={size} value={size}>
              {getPaperSizeDisplayName(size)}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderShelfLabelFields = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Innholdstype
        </label>
        <select
          value={shelfLabelContentType}
          onChange={(e) => setShelfLabelContentType(Number(e.target.value) as ShelfLabelContentType)}
          className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {[
            ShelfLabelContentType.ItemNumberOnly,
            ShelfLabelContentType.ItemNumberWithBarcode,
            ShelfLabelContentType.LocationNameOnly,
            ShelfLabelContentType.LocationNameWithBarcode
          ].map((type) => (
            <option key={type} value={type}>
              {getShelfLabelContentTypeDisplayName(type)}
            </option>
          ))}
        </select>
      </div>

      {(shelfLabelContentType === ShelfLabelContentType.ItemNumberOnly ||
        shelfLabelContentType === ShelfLabelContentType.ItemNumberWithBarcode) && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Artikkelnummer *
            </label>
            <input
              type="text"
              value={shelfItemNo}
              onChange={(e) => setShelfItemNo(e.target.value)}
              placeholder="F.eks. ITEM001"
              className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Beskrivelse (valgfritt)
            </label>
            <input
              type="text"
              value={shelfItemDescription}
              onChange={(e) => setShelfItemDescription(e.target.value)}
              placeholder="F.eks. Demo produkt"
              className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </>
      )}

      {(shelfLabelContentType === ShelfLabelContentType.LocationNameOnly ||
        shelfLabelContentType === ShelfLabelContentType.LocationNameWithBarcode) && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lokasjonskode *
            </label>
            <input
              type="text"
              value={shelfBinCode}
              onChange={(e) => setShelfBinCode(e.target.value)}
              placeholder="F.eks. A-01-01"
              className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lokasjonsnavn (valgfritt)
            </label>
            <input
              type="text"
              value={shelfLocationName}
              onChange={(e) => setShelfLocationName(e.target.value)}
              placeholder="F.eks. Reol A, Hylle 1"
              className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </>
      )}
    </div>
  );

  const renderItemLabelFields = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Artikkelnummer *
        </label>
        <input
          type="text"
          value={itemNo}
          onChange={(e) => setItemNo(e.target.value)}
          placeholder="F.eks. ITEM001"
          className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Beskrivelse *
        </label>
        <input
          type="text"
          value={itemDescription}
          onChange={(e) => setItemDescription(e.target.value)}
          placeholder="F.eks. Demo produkt"
          className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Batch (valgfritt)
          </label>
          <input
            type="text"
            value={itemBatch}
            onChange={(e) => setItemBatch(e.target.value)}
            placeholder="F.eks. B12345"
            className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Antall (valgfritt)
          </label>
          <input
            type="number"
            value={itemQuantity || ''}
            onChange={(e) => setItemQuantity(e.target.value ? Number(e.target.value) : undefined)}
            placeholder="10"
            className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );

  const renderColliLabelFields = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ordrenummer *
        </label>
        <input
          type="text"
          value={colliOrderNoERP}
          onChange={(e) => setColliOrderNoERP(e.target.value)}
          placeholder="F.eks. ORD12345"
          className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Kundenavn *
        </label>
        <input
          type="text"
          value={colliCustomerName}
          onChange={(e) => setColliCustomerName(e.target.value)}
          placeholder="F.eks. Demo Kunde AS"
          className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Leveringsadresse (valgfritt)
        </label>
        <input
          type="text"
          value={colliDeliveryAddress}
          onChange={(e) => setColliDeliveryAddress(e.target.value)}
          placeholder="F.eks. Testveien 123"
          className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Postnummer
          </label>
          <input
            type="text"
            value={colliPostalCode}
            onChange={(e) => setColliPostalCode(e.target.value)}
            placeholder="0123"
            className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sted
          </label>
          <input
            type="text"
            value={colliCity}
            onChange={(e) => setColliCity(e.target.value)}
            placeholder="Oslo"
            className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kolli nummer
          </label>
          <input
            type="number"
            min="1"
            value={colliNumber}
            onChange={(e) => setColliNumber(Number(e.target.value))}
            className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Totalt antall kolli
          </label>
          <input
            type="number"
            min="1"
            value={colliTotal}
            onChange={(e) => setColliTotal(Number(e.target.value))}
            className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          SSCC (valgfritt)
        </label>
        <input
          type="text"
          value={colliSSCC}
          onChange={(e) => setColliSSCC(e.target.value)}
          placeholder="F.eks. 123456789012345678"
          className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>
  );

  const renderPackingSlipInfo = () => (
    <div className={pickingDetails ? "bg-green-50 border border-green-200 rounded-lg p-4" : "bg-blue-50 border border-blue-200 rounded-lg p-4"}>
      {pickingDetails && pickingDetails.lines && pickingDetails.lines.length > 0 ? (
        <>
          <p className="text-sm text-green-800">
            <strong>✓ Faktiske data:</strong> Pakkseddel vil bli generert med data fra pågående plukk.
          </p>
          <div className="text-sm text-green-700 mt-2 space-y-1">
            <p>Plukkliste ID: <strong>{pickingDetails.header.pListID}</strong></p>
            <p>Antall linjer: <strong>{pickingDetails.lines.length}</strong></p>
            <p>Status: <strong>{pickingDetails.header.statusText}</strong></p>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-blue-800">
            <strong>Demo-modus:</strong> Pakkseddel vil bli generert med demo-data.
          </p>
          {pListId && (
            <p className="text-sm text-blue-700 mt-2">
              Plukkliste ID: <strong>{pListId}</strong>
            </p>
          )}
        </>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-3xl overflow-hidden flex flex-col sm:max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Printer className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Skriv ut rapport</h2>
              <p className="text-sm text-gray-500">{documentTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Data source indicator */}
          {pickingDetails && pickingDetails.lines && pickingDetails.lines.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
              <div className="text-sm text-green-800">
                <strong>Automatisk utfylling:</strong> Felt er pre-fylt med data fra pågående plukk (PList #{pickingDetails.header.pListID})
              </div>
            </div>
          )}

          {/* Report Type Selection */}
          {renderReportTypeSelector()}

          {/* Paper Size Selection */}
          {renderPaperSizeSelector()}

          {/* Report-specific fields */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {getReportTypeDisplayName(selectedReportType)} - Innstillinger
            </h3>
            {selectedReportType === ReportType.ShelfLabel && renderShelfLabelFields()}
            {selectedReportType === ReportType.ItemLabel && renderItemLabelFields()}
            {selectedReportType === ReportType.ColliLabel && renderColliLabelFields()}
            {selectedReportType === ReportType.PackingSlip && renderPackingSlipInfo()}
          </div>

          {/* Printer Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Velg skriver
            </label>
            {loadingPrinters ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-blue-600" size={32} />
              </div>
            ) : printers.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Ingen skrivere funnet</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Sørg for at PrintNode-klienten kjører på en datamaskin og at skrivere er tilgjengelige.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {printers.map((printer) => (
                  <label
                    key={printer.id}
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedPrinterId === printer.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="printer"
                      value={printer.id}
                      checked={selectedPrinterId === printer.id}
                      onChange={() => setSelectedPrinterId(printer.id)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <Printer size={18} className="text-gray-600" />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{printer.name}</div>
                      <div className="text-sm text-gray-500">
                        {getComputerName(printer.computerId)}
                        {printer.description && ` • ${printer.description}`}
                      </div>
                    </div>
                    {printer.state === 'online' && (
                      <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded">
                        Tilgjengelig
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Copies */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Antall kopier
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={copies}
              onChange={(e) => setCopies(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
              className="w-24 px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                <p className="text-sm text-green-800">Rapport sendt til utskrift!</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-3 sm:py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 order-2 sm:order-1"
          >
            Avbryt
          </button>
          <button
            onClick={handlePrint}
            disabled={loading || !selectedPrinterId || loadingPrinters}
            className="px-4 py-3 sm:py-2 min-h-[44px] text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 order-1 sm:order-2"
          >
            {loading && <Loader2 className="animate-spin" size={16} />}
            {loading ? 'Sender...' : 'Skriv ut'}
          </button>
        </div>
      </div>
    </div>
  );
};
